// Imports
import React, { useContext, useMemo } from 'react';
import Tippy from '@tippyjs/react';
import tippyProps from '../../../shareables/foundation/front-end/utils/tippy-props';
import APIButton, { APIButtonOptions } from '../../../shareables/integrations/api/components/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import LinkButton from '../../../shareables/foundation/front-end/components/buttons/link';
import { GetWebsitesResponse } from '../../../shareables/types/api/main/reseller/websites/get';
import formatCurrency from '../../../shareables/utils/project-specific/formatting/format-currency';
import ResellerContext from '../../resources/reseller-provider/context';
import planMapping from '../../../shareables/utils/billing/plan-mapping';
import planNameMapping from '../../../shareables/utils/billing/plan-name-mapping';


// Define the accepted props
interface Props {
	website: GetWebsitesResponse[0];
	onChange: () => void;
	storybookDisplayTaxVerbiage?: true;
}


// Function component
const WebsitesRow: React.FC<Props> = ({ website, onChange, storybookDisplayTaxVerbiage }) => {
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Build button options
	const removeButtonOptions: APIButtonOptions<undefined, undefined, undefined> = {
		action: 'REMOVE',
		uri: `/reseller/${reseller?.id ?? ''}/website/${website.id}`,
		warning: {
			message: 'You will no longer be billed for this website.',
			button: `Yes, remove “${website.communityName}”`,
		},
		tooltip: 'Remove this website from your reseller account',
	};
	
	
	// Build renewal cell JSX
	const renewalCellJSX = useMemo(() => {
		const renewalDate = new Date(website.renews);
		let jsx: string | JSX.Element = renewalDate.toLocaleDateString(undefined, {
			timeZone: 'UTC',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
		
		if (website.renews < new Date(Date.now()).toISOString().split('T')[0]) {
			jsx = <span className='is-red'>{jsx}</span>;
		}
		
		return <td>{jsx}</td>;
	}, [website.renews]);
	
	
	// Decide whether tax verbiage should be displayed
	const displaySalesTaxVerbiage = reseller?.regionRequiresSalesTax || storybookDisplayTaxVerbiage;
	
	
	// Return JSX
	return (
		<tr>
			<td className='allow-wrapping'>
				{/* eslint-disable-next-line react/jsx-no-target-blank */}
				<a href={website.url} target='_blank'>
					{website.communityName}
				</a>
			</td>
			
			<td>
				{formatCurrency(website.price, { hideCentsIfInteger: true })}/mo{displaySalesTaxVerbiage ? ' + tax' : ''}
			</td>
			
			{renewalCellJSX}
			
			<td style={{ textAlign: 'center' }}>
				{planNameMapping[website.effectivePlan] === planNameMapping[planMapping.deluxe] &&
				planNameMapping[website.subscribedPlan] === planNameMapping[planMapping.deluxe] ? (
					<small>
						<small>
							<em>
								Deluxe plan;
								<br />
								cannot change
							</em>
						</small>
					</small>
				) : (
					<Tippy
						{...tippyProps}
						content={`Change this website’s plan${
							website.subscribedPlan !== website.effectivePlan
								? ` (currently ${planNameMapping[website.effectivePlan]}, changing to ${planNameMapping[website.subscribedPlan]})`
								: ''
						}`}
					>
						<LinkButton
							to={`${website.id}/change-subscription`}
							color='plain'
							aria-label={`Change the plan of “${website.communityName}”${
								website.subscribedPlan !== website.effectivePlan
									? ` (currently ${planNameMapping[website.effectivePlan]}, changing to ${planNameMapping[website.subscribedPlan]})`
									: ''
							}`}
							border
							smaller
						>
							{planNameMapping[website.effectivePlan]}
							{website.subscribedPlan !== website.effectivePlan && ` → ${planNameMapping[website.subscribedPlan]}`}
						</LinkButton>
					</Tippy>
				)}
			</td>
			
			<td style={{ textAlign: 'center' }}>
				<Tippy
					{...tippyProps}
					content={`Change this website’s size${
						website.subscribedSize !== website.effectiveSize
							? ` (currently ${website.effectiveSize.toLocaleString()}, changing to ${website.subscribedSize.toLocaleString()})`
							: ''
					}`}
				>
					<LinkButton
						to={`${website.id}/change-subscription`}
						color='plain'
						aria-label={`Change the size of “${website.communityName}”${
							website.subscribedSize !== website.effectiveSize
								? ` (currently ${website.effectiveSize.toLocaleString()}, changing to ${website.subscribedSize.toLocaleString()})`
								: ''
						}`}
						border
						smaller
					>
						{website.effectiveSize.toLocaleString()}
						{website.subscribedSize !== website.effectiveSize && ` → ${website.subscribedSize.toLocaleString()}`}
					</LinkButton>
				</Tippy>
			</td>
			
			<td style={{ textAlign: 'center' }}>
				<APIButton
					options={removeButtonOptions}
					onCompletion={onChange}
					aria-label={`Remove “${website.communityName}”`}
					color='danger'
					smaller
				>
					<FontAwesomeIcon icon={faTrash} />
				</APIButton>
			</td>
		</tr>
	);
};

export default WebsitesRow;
